/**
 * Strongly-typed query key factory for every data domain in ReffConnect.
 *
 * Rules:
 *  1. Every `useQuery` / `useMutation` in the app references keys from here —
 *     never inline string arrays.
 *  2. Keys are hierarchical so `queryClient.invalidateQueries({ queryKey: queryKeys.requests.all() })`
 *     invalidates all request sub-types (referrals, resume-reviews, mock-interviews).
 *  3. The `as const` assertion makes keys readonly tuples, giving full type-safety
 *     in `queryClient.setQueryData` and `getQueryData`.
 */

export const queryKeys = {
  // ── Auth & User ──────────────────────────────────────────
  auth: {
    all:                () => ['auth'] as const,
    profile:            (uid: string) => ['auth', 'profile', uid] as const,
    onboarding:         (uid: string) => ['auth', 'onboarding', uid] as const,
    verificationStatus: () => ['auth', 'verification-status'] as const,
    currentUser:        () => ['auth', 'current-user'] as const,
  },

  // ── Platform ─────────────────────────────────────────────
  platform: {
    features: () => ['platform', 'features'] as const,
  },

  // ── Marketplace & Referrers ──────────────────────────────
  marketplace: {
    all:            () => ['marketplace'] as const,
    baseData:       () => ['marketplace', 'base-data'] as const,
    referrers:      () => ['marketplace', 'referrers'] as const,
    professionals:  (serviceType: string) =>
      ['marketplace', 'professionals', serviceType] as const,
  },

  // ── Service Configurations ───────────────────────────────
  serviceConfigs: {
    all:         () => ['service-configs'] as const,
    mine:        () => ['service-configs', 'mine'] as const,
    forReferrer: (referrerId: string) =>
      ['service-configs', 'referrer', referrerId] as const,
    forService:  (serviceId: string) =>
      ['service-configs', 'service', serviceId] as const,
  },

  // ── Requests (Job Seeker Side — requester) ───────────────
  requests: {
    all:            () => ['requests'] as const,
    tracker:        () => ['requests', 'tracker'] as const,
    referrals:      () => ['requests', 'referrals'] as const,
    resumeReviews:  () => ['requests', 'resume-reviews'] as const,
    mockInterviews: () => ['requests', 'mock-interviews'] as const,
  },

  // ── Requests (Referrer Side — provider) ──────────────────
  referrerRequests: {
    all:            () => ['referrer-requests'] as const,
    referrals:      () => ['referrer-requests', 'referrals'] as const,
    resumeReviews:  () => ['referrer-requests', 'resume-reviews'] as const,
    mockInterviews: () => ['referrer-requests', 'mock-interviews'] as const,
  },

  // ── Sessions ─────────────────────────────────────────────
  sessions: {
    all:            () => ['sessions'] as const,
    referral:       () => ['sessions', 'referral'] as const,
    mockInterview:  () => ['sessions', 'mock-interview'] as const,
    forReferrer:    (refId: string, type: string) =>
      ['sessions', 'for-referrer', refId, type] as const,
    reservations:   (refId: string) =>
      ['sessions', 'reservations', refId] as const,
    byId:           (type: string, id: number) =>
      ['sessions', type, id] as const,
  },

  // ── Jobs ─────────────────────────────────────────────────
  jobs: {
    all:    () => ['jobs'] as const,
    list:   (page: number, perPage: number, filters?: Record<string, unknown>) =>
      ['jobs', 'list', page, perPage, filters] as const,
    stats:  () => ['jobs', 'stats'] as const,
    detail: (jobId: string) => ['jobs', 'detail', jobId] as const,
    // Professional side: my own postings + applicants for one posting.
    myPostings:     () => ['jobs', 'my-postings'] as const,
    applicants:     (jobId: number) => ['jobs', 'applicants', jobId] as const,
    // Seeker side: my job applications + whether I've applied to a given job.
    myApplications: () => ['jobs', 'my-applications'] as const,
    myApplication:  (jobId: number) => ['jobs', 'my-application', jobId] as const,
    // External openings ingested from company career pages.
    external:       (company?: string) => ['jobs', 'external', company ?? 'all'] as const,
  },

  // ── Weekly Drops ─────────────────────────────────────────
  drops: {
    all:     () => ['drops'] as const,
    active:  (search?: string) => ['drops', 'active', search] as const,
    myCards: () => ['drops', 'my-cards'] as const,
  },


  // ── Dashboard Summaries ──────────────────────────────────
  dashboard: {
    referrer:  (uid: string) => ['dashboard', 'referrer', uid] as const,
    jobseeker: (uid: string) => ['dashboard', 'jobseeker', uid] as const,
  },

  // ── Signed Storage URLs ──────────────────────────────────
  signedUrl: (bucket: string, path: string) =>
    ['signed-url', bucket, path] as const,

  // ── Notifications ────────────────────────────────────────
  notifications: () => ['notifications'] as const,
} as const;
